import {
  BILLING_PLAN_IDS,
  type BillingInterval,
  type BillingPlanId,
  type BillingPlanPrice,
} from "@/services/api/billingService"

export const findPrice = (
  plans: readonly BillingPlanPrice[],
  plan: BillingPlanId,
  interval: BillingInterval,
): BillingPlanPrice | undefined =>
  plans.find((price) => price.plan === plan && price.interval === interval)

export interface YearlySavings {
  /** Whole percent saved by paying yearly instead of 12 monthly payments. */
  percent: number
  /** Minor units saved per year. */
  amount: number
  currency: string
}

/** What yearly billing saves against 12 monthly payments; null when either
 *  price is missing or yearly is not cheaper. */
export function yearlySavings(
  plans: readonly BillingPlanPrice[],
  plan: BillingPlanId,
): YearlySavings | null {
  const month = findPrice(plans, plan, "month")
  const year = findPrice(plans, plan, "year")
  if (!month || !year || month.currency !== year.currency) return null
  const amount = month.unitAmount * 12 - year.unitAmount
  if (amount <= 0 || month.unitAmount <= 0) return null
  const percent = Math.round((amount / (month.unitAmount * 12)) * 100)
  return { percent, amount, currency: year.currency }
}

export function formatMoney(
  minorUnits: number,
  currency: string,
  locale: string,
): string {
  const fractionless = minorUnits % 100 === 0
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: fractionless ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100)
}

/** The largest yearly saving across the plans, in whole percent; null when no
 *  plan has one. */
export function maxYearlySavingsPercent(
  plans: readonly BillingPlanPrice[],
): number | null {
  const percents = BILLING_PLAN_IDS.flatMap((plan) => {
    const savings = yearlySavings(plans, plan)
    return savings ? [savings.percent] : []
  })
  return percents.length ? Math.max(...percents) : null
}
